import {FC, useCallback} from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useBackend} from 'state/backend';
import MailpointManualInput from '../MailpointManualInput';
import MailpointPresetOptions from '../MailpointPresetOptions';
import {MailpointsForm, MailpointsInitialData} from '../types';

const MailpointSelection: FC = () => {
    const {data} = useBackend<MailpointsInitialData>();

    const {setValue, watch} = useFormContext<MailpointsForm>();
    const {t} = useTranslation('Billing.Mailpoints');

    const [mailpoints, mode] = watch(['mailpoints', 'mode']);

    const onClick = useCallback(() => {
        // Toggle mode
        const newMode = mode === 'presets' ? 'manual' : 'presets';
        setValue('mode', newMode);

        if (!mailpoints) return null;

        let newMailpoints: string | undefined = mailpoints;

        // If switching between Preset and Manual (x10MP)
        if (data.onlyMultiplesOfTenAllowed) {
            newMailpoints =
                newMode === 'manual'
                    ? String(Number(mailpoints) / 10)
                    : String(Number(mailpoints) * 10);
        }

        // If switching from Manual to Preset
        if (
            newMode === 'presets' &&
            !data.mpOptions.includes(Number(newMailpoints))
        ) {
            newMailpoints = undefined;
        }

        // Set Mailpoint values
        return setValue('mailpoints', newMailpoints);
    }, [
        data.mpOptions,
        data.onlyMultiplesOfTenAllowed,
        mailpoints,
        mode,
        setValue,
    ]);

    return (
        <div className="flex flex-col items-start gap-y-2">
            <MailpointPresetOptions
                hide={mode !== 'presets'}
                options={data.mpOptions}
            />
            <MailpointManualInput
                hide={mode !== 'manual'}
                isOnlyMultiplesOfTenAllowed={data.onlyMultiplesOfTenAllowed}
                max={
                    data.onlyMultiplesOfTenAllowed
                        ? data.mpPackMaximum
                        : data.mpMaximum
                }
                min={
                    data.onlyMultiplesOfTenAllowed
                        ? data.mpPackMinimum
                        : data.mpMinimum
                }
            />
            <button
                className="text-green-800 hover:text-green-800 hover:underline"
                onClick={onClick}
            >
                {mode === 'manual' ? t('selectAmount') : t('enterAmount')}
            </button>
        </div>
    );
};

export default MailpointSelection;
